import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAiChatSession from "../../../hooks/useAiChatSession";
import { pathConstants } from "../../../routes/pathContants";
import { AI_SUGGESTED_PROMPTS_COMPACT } from "../../../helpers/constants";
import { formatMessageTime } from "../../../helpers";
import { TemiSparkIcon } from "../../../assets/svgIcons";
import AssistantMarkdown from "../AssistantMarkdown";
import InteractivePrompt from "../../../pages/appPages/LumiAiAssistant/InteractivePrompt";
import styles from "./FloatingAiAssistant.module.scss";

// Temi docked over whatever page the merchant is on. It is the same assistant
// as the full page and the same stored conversation — "Expand" hands the live
// thread over by opening that conversation's own route.
const FloatingAiAssistant = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const close = useCallback(() => setIsOpen(false), []);
  const rootRef = useRef(null);

  const {
    messageInput,
    setMessageInput,
    localMessages,
    isStreaming,
    isSending,
    isLoadingDetail,
    isAiBlocked,
    blockedReason,
    canTopUpForBlock,
    activeConversationId,
    senderName,
    messagesEndRef,
    inputRef,
    handleSendMessage,
    handleSuggestedPrompt,
    handleKeyDown,
    handleNewChat,
    handleTopUpCredit,
  } = useAiChatSession({ syncWithUrl: false, isEnabled: isOpen });

  const isBusy = isSending || isStreaming;

  // The thread lives on the server, so moving to the full page is just a
  // matter of opening this conversation there. A chat with nothing in it yet
  // has no id, and lands on the empty assistant page.
  const expandToFullPage = useCallback(() => {
    close();
    navigate(
      activeConversationId
        ? `${pathConstants.LUMI_AI_ASSISTANT}/${activeConversationId}`
        : pathConstants.LUMI_AI_ASSISTANT,
    );
  }, [activeConversationId, close, navigate]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === "Escape") close();
    };

    // Closing on `mousedown` rather than `click` matters: by the time a click
    // bubbles to the document, React has already re-rendered, so a control
    // that removes itself — a suggested prompt replaced by the conversation —
    // is gone from the tree and would look like an outside click.
    const handleOutside = (e) => {
      if (!rootRef.current || rootRef.current.contains(e.target)) return;
      close();
    };

    document.addEventListener("keydown", handleEscape);
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [isOpen, close]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen, inputRef]);

  // The full assistant page is its own chat — a launcher over it is redundant
  if (pathname.startsWith(pathConstants.LUMI_AI_ASSISTANT)) return null;

  return (
    <div className={styles.floatingAi} ref={rootRef}>
      {isOpen ? (
        <div
          className={styles.floatingAi__panel}
          role="dialog"
          aria-label="Ask Temi"
        >
          <div className={styles.floatingAi__header}>
            <div className={styles.floatingAi__identity}>
              <span className={styles.floatingAi__avatar}>
                <TemiSparkIcon width="16" height="16" />
              </span>
              <span className={styles.floatingAi__headerTitle}>Temi</span>
              <span className={styles.floatingAi__badge}>Beta</span>
            </div>

            <div className={styles.floatingAi__headerActions}>
              <button
                className={styles.floatingAi__expand}
                onClick={expandToFullPage}
                title="Open in full page"
              >
                Expand ↗
              </button>
              <button
                className={styles.floatingAi__headerBtn}
                onClick={handleNewChat}
                title="New chat"
                aria-label="New chat"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </button>
              <button
                className={styles.floatingAi__headerBtn}
                onClick={close}
                title="Close"
                aria-label="Close assistant"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className={styles.floatingAi__messages}>
            {isLoadingDetail ? (
              <p className={styles.floatingAi__empty}>
                Loading conversation...
              </p>
            ) : localMessages.length === 0 ? (
              <div className={styles.floatingAi__empty}>
                <p>Ask me anything about your business.</p>

                <div className={styles.floatingAi__prompts}>
                  {AI_SUGGESTED_PROMPTS_COMPACT.map((prompt) => (
                    <button
                      key={prompt}
                      className={styles.floatingAi__prompt}
                      onClick={() => handleSuggestedPrompt(prompt)}
                      disabled={isBusy || isAiBlocked}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              localMessages.map((msg, idx) => {
                const time = formatMessageTime(msg.at);

                if (msg.role === "user")
                  return (
                    <div key={idx} className={styles.floatingAi__userTurn}>
                      <div className={styles.floatingAi__meta}>
                        <span className={styles.floatingAi__metaName}>
                          {senderName}
                        </span>
                        {time && (
                          <span className={styles.floatingAi__metaTime}>
                            {time}
                          </span>
                        )}
                      </div>
                      <div className={styles.floatingAi__bubble}>
                        {msg.content}
                      </div>
                    </div>
                  );

                return (
                  <div key={idx} className={styles.floatingAi__aiTurn}>
                    <div className={styles.floatingAi__meta}>
                      <span className={styles.floatingAi__metaName}>Temi</span>
                      {time && (
                        <span className={styles.floatingAi__metaTime}>
                          {time}
                        </span>
                      )}
                    </div>

                    {msg.isLoading ? (
                      <div className={styles.floatingAi__loading}>
                        <span />
                        <span />
                        <span />
                      </div>
                    ) : (
                      <>
                        <div
                          className={
                            msg.isError
                              ? styles["floatingAi__aiTurn--error"]
                              : undefined
                          }
                        >
                          <AssistantMarkdown content={msg.content} />
                          {msg.isStreaming && (
                            <span className={styles.floatingAi__cursor} />
                          )}
                        </div>
                        {/* Only the latest turn can still be answered — once a
                            new message follows it, the picker it asked with is stale. */}
                        {!msg.isStreaming &&
                          !!msg.content &&
                          msg.interactive_prompt &&
                          idx === localMessages.length - 1 && (
                            <InteractivePrompt
                              prompt={msg.interactive_prompt}
                              onAnswer={handleSuggestedPrompt}
                              disabled={isBusy || isAiBlocked}
                            />
                          )}
                        {!msg.isStreaming && !!msg.content && (
                          <button
                            className={styles.floatingAi__continue}
                            onClick={expandToFullPage}
                          >
                            Continue in full view ↗
                          </button>
                        )}
                      </>
                    )}
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {isAiBlocked && (
            <div className={styles.floatingAi__blocked}>
              <p>{blockedReason}</p>
              {/* A daily-limit block cannot be lifted by paying */}
              {canTopUpForBlock && (
                <button onClick={handleTopUpCredit}>Top up credit</button>
              )}
            </div>
          )}

          <div className={styles.floatingAi__composerWrap}>
            <div className={styles.floatingAi__composer}>
              <textarea
                ref={inputRef}
                className={styles.floatingAi__composerField}
                placeholder={
                  isAiBlocked
                    ? canTopUpForBlock
                      ? "Top up your credit to continue chatting"
                      : "Today's AI limit is spent, it resets at midnight"
                    : "Message Temi…"
                }
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
                disabled={isBusy || isAiBlocked}
              />
              <button
                className={styles.floatingAi__send}
                onClick={handleSendMessage}
                disabled={!messageInput.trim() || isBusy || isAiBlocked}
                title="Send message"
                aria-label="Send message"
              >
                <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                  <path
                    d="M3 9H15M15 9L10 4M15 9L10 14"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            <p className={styles.floatingAi__disclaimer}>
              Temi can make mistakes.
            </p>
          </div>
        </div>
      ) : (
        // Closing the panel leaves the pill behind, never a bare icon button
        <button
          className={styles.floatingAi__launcher}
          onClick={() => setIsOpen(true)}
          title="Ask Temi"
          aria-label="Ask Temi"
          aria-expanded={false}
          aria-haspopup="dialog"
        >
          <TemiSparkIcon
            width="22"
            height="22"
            primaryColor="#FFFFFF"
            secondaryColor="#C8B5F8"
          />
          Ask Temi
        </button>
      )}
    </div>
  );
};

export default FloatingAiAssistant;
